import type { ReactNode } from "react";

type SelectionState = "selected" | "unselected" | "partiallySelected";

export interface ITreeNode {
    id: string;
    children?: ITreeNode[];
    state?: SelectionState;
}

export type InnerIterateTree<T extends ITreeNode = ITreeNode> = (
    item: T,
    childLevel: number,
    parentItem?: T,
) => ReactNode;

function findNode<T extends ITreeNode = ITreeNode>(currentTree: T[], id: string): T | undefined {
    for (const node of currentTree) {
        if (node.id === id) {
            return node;
        }

        if (node.children) {
            const found = findNode<T>(node.children as T[], id);

            if (found) {
                return found;
            }
        }
    }
}

function updateNodeAndChildrenState<T extends ITreeNode = ITreeNode>(
    node: T,
    newState: Exclude<SelectionState, "partiallySelected">,
): T {
    const newNode = {
        ...node,
        state: newState,
    };

    if (!node.children) {
        return newNode;
    }

    return {
        ...newNode,
        children: node.children.map((child: T) => updateNodeAndChildrenState<T>(child, newState)),
    };
}

function getStateFromChildren<T extends ITreeNode = ITreeNode>(children: T[]): SelectionState {
    const childStates = children.map((c) => c.state);

    if (childStates.every((s) => s === "selected")) {
        return "selected";
    }

    if (childStates.some((s) => s === "selected" || s === "partiallySelected")) {
        return "partiallySelected";
    }

    return "unselected";
}

// update states in tree
function updateTree<T extends ITreeNode = ITreeNode>(
    currentTree: T[],
    selectedIds: string[],
    shouldBeSelected?: boolean,
): T[] {
    return currentTree.map((node) => {
        const shouldSelect = shouldBeSelected || selectedIds.includes(node.id);

        if (!node.children) {
            return { ...node, state: shouldSelect ? "selected" : "unselected" };
        }

        const updatedChildren = updateTree(node.children as T[], selectedIds, shouldSelect);

        if (shouldSelect) {
            return { ...node, state: "selected", children: updatedChildren };
        }

        const state = getStateFromChildren(updatedChildren);

        return { ...node, state, children: updatedChildren };
    });
}

function updateStateByItemId<T extends ITreeNode = ITreeNode>(
    currentTree: T[],
    itemId: string,
    newState: Exclude<SelectionState, "partiallySelected">,
): T[] {
    return currentTree.map((node) => {
        if (node.id === itemId) {
            return updateNodeAndChildrenState(node, newState);
        }

        if (!node.children) {
            return node;
        }

        const updatedChildren = updateStateByItemId(node.children, itemId, newState);

        const state = getStateFromChildren(updatedChildren);

        return { ...node, state, children: updatedChildren };
    });
}

// initialization of tree
function initializeTreeState<T extends ITreeNode = ITreeNode>(currentTree: T[]): T[] {
    return currentTree.map((node): T => {
        const children = node.children ? initializeTreeState(node.children) : undefined;
        let state: SelectionState = node.state ?? "unselected";

        if (children) {
            const childStates = children.map((c) => c.state);

            if (childStates.every((s) => s === "selected")) {
                state = "selected";
            } else if (childStates.some((s) => s === "selected" || s === "partiallySelected")) {
                state = "partiallySelected";
            } else {
                state = "unselected";
            }
        }

        return { ...node, state, children } as T;
    });
}

function getUpdatedSelectionById<T extends ITreeNode = ITreeNode>(currentTree: T[], itemId: string): T[] {
    const node = findNode<T>(currentTree, itemId);

    if (!node) {
        return currentTree;
    }

    const newState: Exclude<SelectionState, "partiallySelected"> =
        node.state === "selected" ? "unselected" : "selected";

    return updateStateByItemId(currentTree, itemId, newState);
}

// selectedList: selected items, only selected parent without children if all children are selected
function getSelectedList(nodes: ITreeNode[]): Record<string, true> {
    const result: Record<string, true> = {};

    const walk = (items: ITreeNode[]) => {
        for (const item of items) {
            if (item.state === "selected") {
                if (!item.children || item.children.every((c) => c.state === "selected")) {
                    // Додаємо лише батька
                    result[item.id] = true;
                } else if (item.children) {
                    // Діти не всі selected → рекурсивно перевіряємо дітей
                    walk(item.children);
                }
            } else if (item.state === "partiallySelected" && item.children) {
                // для partiallySelected рекурсивно перевіряємо дітей
                walk(item.children);
            }
        }
    };

    walk(nodes);

    return result;
}

// allSelectedItems: all selected items including selected children
function getAllSelectedItems(nodes: ITreeNode[]): Record<string, true> {
    const all: Record<string, true> = {};

    const addAllDescendants = (node: ITreeNode) => {
        if (node.state === "selected") {
            all[node.id] = true;
        }
        if (node.children) {
            node.children.forEach(addAllDescendants);
        }
    };

    const walk = (items: ITreeNode[]) => {
        for (const item of items) {
            if (item.state === "selected") {
                addAllDescendants(item);
            } else if (item.state === "partiallySelected" && item.children) {
                walk(item.children);
            }
        }
    };

    walk(nodes);

    return all;
}

// selectedChildren: all selected items without parents items
function getSelectedChildren(nodes: ITreeNode[]): Record<string, true> {
    const children: Record<string, true> = {};

    const walk = (items: ITreeNode[]) => {
        for (const item of items) {
            if (item.state === "selected" && (!item.children || item.children.length === 0)) {
                children[item.id] = true;
            }

            if (item.children) {
                walk(item.children);
            }
        }
    };

    walk(nodes);

    return children;
}

// recursive iterate the tree
function innerIterateTree<T extends ITreeNode = ITreeNode>(
    renderItem: InnerIterateTree,
    tree: T[],
    depth = 0,
    parent?: ITreeNode,
): ReactNode[] {
    return tree.flatMap((node) => [
        renderItem(node, depth, parent),
        ...(node.children ? innerIterateTree(renderItem, node.children, depth + 1, node) : []),
    ]);
}

function iterateTree<T extends ITreeNode = ITreeNode>(renderItem: InnerIterateTree<T>, tree: T[]): ReactNode[] {
    return innerIterateTree(renderItem, tree);
}

const selectableTreeService = {
    updateTree,
    initializeTreeState,
    getUpdatedSelectionById,
    iterateTree,
    getSelectedList,
    getAllSelectedItems,
    getSelectedChildren,
};

export default selectableTreeService;
